/**
 * Transaction data generator matching new BFF API spec
 */

import { faker } from '@faker-js/faker'
import type {
  Transaction,
  TransactionStatus,
  TypeCharge,
  Currency,
  RefundData
} from '../../app/types/transactions'
import { COUNTRY_COORDS } from '../../app/utils/country-coords'

// Valid country codes for mock data (must exist in COUNTRY_COORDS for map visualization)
const VALID_COUNTRY_CODES = Object.keys(COUNTRY_COORDS)

// Seed for reproducibility
faker.seed(12345)

// Status distribution
const STATUS_WEIGHTS: Array<{ value: TransactionStatus, weight: number }> = [
  { value: 'paid', weight: 0.58 },
  { value: 'error', weight: 0.20 },
  { value: 'pending', weight: 0.10 },
  { value: 'refunded', weight: 0.05 },
  { value: 'partially_refunded', weight: 0.04 },
  { value: 'canceled', weight: 0.02 },
  { value: 'confirmed', weight: 0.01 }
]

// Payment types - always credit_card for consistent visual captures
const TYPE_CHARGE_WEIGHTS: Array<{ value: TypeCharge, weight: number }> = [
  { value: 'credit_card', weight: 1.0 }
]

// Currencies
const CURRENCY_WEIGHTS: Array<{ value: Currency, weight: number }> = [
  { value: 'BRL', weight: 0.50 },
  { value: 'USD', weight: 0.30 },
  { value: 'MXN', weight: 0.15 },
  { value: 'CLP', weight: 0.03 },
  { value: 'ARS', weight: 0.02 }
]

// Card brands (now just strings to match API)
const CARD_BRANDS = ['Visa', 'MasterCard', 'Amex', 'Elo']

// Error codes with source field
const ERROR_CODES = [
  { code: 'bad_request_59', source: 'authorization', description: 'Transaction not authorized. Fraud suspicion.' },
  { code: 'bad_request_51', source: 'authorization', description: 'Transaction not authorized. Limit exceeded/insufficient balance.' },
  { code: 'bad_request_57', source: 'authorization', description: 'Transaction not authorized. Invalid card.' },
  { code: 'bad_request_05', source: 'authorization', description: 'Transaction not authorized. Card blocked.' },
  { code: 'bad_request_63', source: 'authorization', description: 'Security violation (invalid or not present)' },
  { code: 'error_threeds_authentication', source: 'authentication', description: 'issued bank declined the authentication' }
]

// Mock accounts
const ACCOUNTS = [
  { id: '1', name: 'PAYSECURE LLC', entity_uuid: faker.string.uuid() },
  { id: '2', name: '3STARS PAY LTDA', entity_uuid: faker.string.uuid() },
  { id: '3', name: 'PAGSMILE', entity_uuid: faker.string.uuid() }
]

// Mock merchants
const MERCHANTS = [
  { id: '1', name: 'HANDMUSE', merchant_uuid: faker.string.uuid() },
  { id: '2', name: 'Iunicharge', merchant_uuid: faker.string.uuid() },
  { id: '3', name: 'CIEclothesfondyouw', merchant_uuid: faker.string.uuid() },
  { id: '4', name: 'PAGSMILE', merchant_uuid: faker.string.uuid() },
  { id: '5', name: 'GlamForce', merchant_uuid: faker.string.uuid() }
]

// Mock wallets
const WALLETS = [
  { id: '1', name: 'Wallet Principal', wallet_uuid: faker.string.uuid() },
  { id: '2', name: 'Wallet Premium', wallet_uuid: faker.string.uuid() },
  { id: '3', name: 'Wallet Business', wallet_uuid: faker.string.uuid() }
]

function weightedRandom<T>(items: Array<{ value: T, weight: number }>): T {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0)
  const random = Math.random() * totalWeight

  let currentWeight = 0
  for (const item of items) {
    currentWeight += item.weight
    if (random <= currentWeight) {
      return item.value
    }
  }

  return items[items.length - 1]!.value
}

function generateTransaction(index: number): Transaction {
  const status = weightedRandom(STATUS_WEIGHTS)
  const typeCharge = weightedRandom(TYPE_CHARGE_WEIGHTS)
  const currency = weightedRandom(CURRENCY_WEIGHTS)
  const amount = faker.number.float({ min: 50, max: 5000, fractionDigits: 2 })

  // Currency conversion rates (to USD)
  const rates: Record<Currency, number> = {
    USD: 1,
    BRL: 0.20,
    MXN: 0.058,
    CLP: 0.0011,
    ARS: 0.0025
  }

  const usdAmount = amount * rates[currency]
  const cardBrand = faker.helpers.arrayElement(CARD_BRANDS)
  const account = faker.helpers.arrayElement(ACCOUNTS)
  const merchant = faker.helpers.arrayElement(MERCHANTS)
  const wallet = faker.helpers.arrayElement(WALLETS)

  const createdAt = faker.date.between({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Last 7 days
    to: new Date()
  }).toISOString()

  const transaction: Transaction = {
    id: String(index + 1),
    external_id: faker.datatype.boolean({ probability: 0.7 }) ? faker.string.uuid() : null,
    charge_uuid: faker.string.uuid(),
    transaction_reference: `2026${String(index + 1).padStart(14, '0')}`,
    tid: status === 'paid' || status === 'confirmed' ? faker.string.alphanumeric(20).toUpperCase() : null,
    authorization_code: status === 'paid' || status === 'confirmed' ? faker.string.numeric(6) : null,
    amount,
    currency,
    usd_amount: Number(usdAmount.toFixed(2)),
    installment_value_total: amount,
    installment_count: 1,
    installment_number: 1,
    installments: [],
    status,
    statement_descriptor: `A55 PAYMENTS LTDA*${merchant.name}`,
    message: status === 'error' ? [faker.helpers.arrayElement(ERROR_CODES)] : [],
    items: [
      {
        sku: `2026${String(index + 1).padStart(14, '0')}`,
        name: faker.commerce.productName(),
        quantity: 1,
        description: faker.commerce.productDescription(),
        unit_amount: amount,
        total_amount: amount
      }
    ],
    created_at: createdAt,
    account,
    merchant,
    wallet,
    customer: {
      id: String(faker.number.int({ min: 900000, max: 1100000 })),
      customer_uuid: faker.string.uuid(),
      name: faker.person.fullName().toUpperCase(),
      email: faker.internet.email().toLowerCase(),
      tax_id: faker.helpers.replaceSymbols('###########'),
      phone: `55${faker.helpers.replaceSymbols('###########')}`,
      address: {
        street: faker.location.streetAddress().toUpperCase(),
        number: faker.location.buildingNumber(),
        city: faker.location.city().toUpperCase(),
        state: faker.location.state({ abbreviated: true }),
        country: 'BR',
        postal_code: faker.helpers.replaceSymbols('########')
      }
    },
    has_authentication: status === 'paid' || status === 'confirmed' ? faker.datatype.boolean() : false,
    is_data_only: faker.datatype.boolean({ probability: 0.1 }),
    has_decision_manager: faker.datatype.boolean({ probability: 0.7 }),
    card_brand: cardBrand,
    card_mask: `${faker.string.numeric(6)} *** **** ${faker.string.numeric(4)}`,
    bin_info: typeCharge === 'credit_card'
      ? {
          bin: faker.string.numeric(6),
          brand: cardBrand.toUpperCase(),
          type: 'CREDIT',
          category: faker.helpers.arrayElement(['CLASSIC', 'GOLD', 'PLATINUM', 'INFINITE']),
          issuer: faker.company.name().toUpperCase(),
          code: faker.helpers.arrayElement(VALID_COUNTRY_CODES),
          country: faker.location.country().toUpperCase()
        }
      : null,
    customer_name: faker.person.fullName(),
    type_charge: typeCharge,
    location: faker.datatype.boolean({ probability: 0.7 })
      ? {
          city: faker.location.city(),
          postal: faker.helpers.replaceSymbols('#####-###'),
          region: faker.location.state(),
          status: 'success',
          country: 'Brazil',
          latitude: Number(faker.location.latitude()),
          continent: 'South America',
          longitude: Number(faker.location.longitude()),
          region_code: faker.location.state({ abbreviated: true }),
          country_code: 'BR'
        }
      : null,
    refund_data: null,
    date_refunded: null
  }

  if (status === 'refunded') {
    const refundDate = faker.date.between({ from: createdAt, to: new Date() }).toISOString()
    const refundData: RefundData = {
      amount_refunded: amount,
      amount_remaining: 0,
      refunds: [{ refund_uuid: faker.string.uuid(), amount, status: 'refunded', created: refundDate }]
    }
    transaction.refund_data = refundData
    transaction.date_refunded = refundDate
  } else if (status === 'partially_refunded') {
    const partialAmount = Number((amount * faker.number.float({ min: 0.2, max: 0.7, fractionDigits: 2 })).toFixed(2))
    const refundDate = faker.date.between({ from: createdAt, to: new Date() }).toISOString()
    transaction.refund_data = {
      amount_refunded: partialAmount,
      amount_remaining: Number((amount - partialAmount).toFixed(2)),
      refunds: [{ refund_uuid: faker.string.uuid(), amount: partialAmount, status: 'refunded', created: refundDate }]
    }
  }

  return transaction
}

// Generate mock transactions
export function generateTransactions(count = 500): Transaction[] {
  return Array.from({ length: count }, (_, i) => generateTransaction(i))
}

// Export singleton instance
let cachedTransactions: Transaction[] | null = null

export function getTransactions(): Transaction[] {
  if (!cachedTransactions) {
    cachedTransactions = generateTransactions()
  }
  return cachedTransactions
}

// Linked transaction type for merchant/wallet relationships
export interface LinkedTransaction {
  id: string
  merchantId: string
  walletId: string
  chargeUuid: string
  amount: number
  status: TransactionStatus
  createdAt: string
  type: TypeCharge
  currency: Currency
  cardBrand: string
  cardMask: string
  customer: {
    name: string
    email: string
  }
}

// Generate linked transactions with merchant and wallet IDs
export function generateLinkedTransactions(
  merchantIds: string[],
  walletsByMerchant: Record<string, string[]>
): LinkedTransaction[] {
  const transactions = getTransactions()

  return transactions.map((tx) => {
    // Pick a random merchant
    const merchantId = faker.helpers.arrayElement(merchantIds)
    // Pick a wallet for that merchant
    const walletIds = walletsByMerchant[merchantId] || []
    const walletId = walletIds.length > 0
      ? faker.helpers.arrayElement(walletIds)
      : merchantId // fallback

    return {
      id: tx.id,
      merchantId,
      walletId,
      chargeUuid: tx.charge_uuid,
      amount: tx.usd_amount,
      status: tx.status,
      createdAt: tx.created_at,
      type: tx.type_charge,
      currency: tx.currency,
      cardBrand: tx.card_brand,
      cardMask: tx.card_mask,
      customer: {
        name: tx.customer.name,
        email: tx.customer.email
      }
    }
  })
}

// Fresh transaction type for real-time feed
export interface FreshTransaction {
  id: string
  amount: number
  status: TransactionStatus
  cardBrand: string
  cardMask: string
  createdAt: string
  typeCharge: TypeCharge
  customer: {
    name: string
    email: string
  }
}

// Generate fresh transactions for real-time feed
export function generateFreshTransactions(count: number): FreshTransaction[] {
  return Array.from({ length: count }, (_, i) => {
    const tx = generateTransaction(Date.now() + i)
    return {
      id: tx.id,
      amount: tx.usd_amount,
      status: tx.status,
      cardBrand: tx.card_brand,
      cardMask: tx.card_mask,
      createdAt: tx.created_at,
      typeCharge: tx.type_charge,
      customer: {
        name: tx.customer.name,
        email: tx.customer.email
      }
    }
  })
}

// Generate transaction summary
export function generateTransactionSummary() {
  const transactions = getTransactions()
  const total = transactions.length
  const paid = transactions.filter(t => t.status === 'paid').length
  const pending = transactions.filter(t => t.status === 'pending').length
  const error = transactions.filter(t => t.status === 'error').length

  return {
    total,
    paid,
    pending,
    error,
    totalAmount: transactions.reduce((sum, t) => sum + t.usd_amount, 0)
  }
}

// Filter options for transaction list
export interface TransactionListFilters {
  status?: TransactionStatus[]
}

// Generate transaction list response
export function generateTransactionListResponse(
  page: number = 1,
  limit: number = 25,
  filters?: TransactionListFilters
) {
  let transactions = getTransactions()

  // Apply filters if provided
  if (filters?.status && filters.status.length > 0) {
    const statusFilter = filters.status!
    transactions = transactions.filter(t => statusFilter.includes(t.status))
  }

  const total = transactions.length
  const start = (page - 1) * limit
  const end = start + limit
  const items = transactions.slice(start, end)

  return {
    data: items,
    meta: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit)
    }
  }
}
